import React, { useState, useCallback, FormEvent } from "react";
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import {
  PageContainer,
  ContentWrapper,
  InfoSection,
  FormSection,
  Title,
  Input,
  Button,
  ContactButtons,
  ContactButton,
  FeatureItem,
  FormsParagr,
  ErrorMessage,
  SuccessMessage,
} from "./forms";
import api from "../../services/api";

interface ValidationErrors {
  [key: string]: string;
}

interface IApiErrorData {
  message?: string;
}

const getValidationErrors = (err: Yup.ValidationError): ValidationErrors => {
  const validationErrors: ValidationErrors = {};
  err.inner.forEach(error => {
    if (error.path) {
      validationErrors[error.path] = error.message;
    }
  });
  return validationErrors;
};

function isAxiosError(error: unknown): error is AxiosError {
  return typeof error === 'object' && error !== null && (error as AxiosError).isAxiosError === true;
}

const FormsProperty = () => {
  const [address, setAddress] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [rentValue, setRentValue] = useState('');
  const [neighborhood, setNeighborhood] = useState('');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [validationError, setValidationError] = useState<ValidationErrors>({});
  const [apiError, setApiError] = useState('');


  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '');

    if (value.length <= 11) {
      if (value.length <= 2) {
        value = value.replace(/^(\d{0,2})/, '($1');
      } else if (value.length <= 7) {
        value = value.replace(/^(\d{2})(\d{0,5})/, '($1) $2');
      } else {
        value = value.replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
      }
      setPhone(value);
    }
  };


  const removePhoneMask = (phone: string): string => {
    return phone.replace(/\D/g, '');
  };

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoading(true);
    setSuccess('');
    setApiError('');
    setValidationError({});

    try {
      const formData = {
        name,
        email,
        phone: removePhoneMask(phone),
        city,
        rent_value: rentValue,
        neighborhood,
        address,
      };

      const schema = Yup.object().shape({
        name: Yup.string().required('Seu nome é obrigatório'),
        email: Yup.string().email('E-mail inválido').required('O e-mail é obrigatório'),
        phone: Yup.string().required('O telefone é obrigatório').min(10, 'Telefone deve ter no mínimo 10 dígitos'),
        city: Yup.string().required('A cidade é obrigatória'),
        rent_value: Yup.string().required('O valor do aluguel é obrigatório'),
        neighborhood: Yup.string().required('O bairro é obrigatório'),
      });

      await schema.validate(formData, {
        abortEarly: false
      });

      const response = await api.post('/api/client/advertise/announceProperty', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.city,
        rent_value: formData.rent_value,
        neighborhood: formData.neighborhood,
        address: formData.address
      });

      if (response.status === 200) {
        setSuccess(response.data.message || "Imóvel cadastrado com sucesso!");
        setAddress('');
        setName('');
        setEmail('');
        setPhone('');
        setCity('');
        setRentValue('');
        setNeighborhood('');
      }

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        setValidationError(errors);
        setApiError('');
      } else if (isAxiosError(err) && err.response) {
        setValidationError({});

        const errorData = err.response.data as IApiErrorData;
        setApiError(errorData.message || 'Erro ao cadastrar imóvel. Tente novamente.');
      } else {
        setValidationError({});
        setApiError('Ocorreu um erro inesperado: ' + String(err));
      }
    } finally {
      setLoading(false);
    }
  }, [address, name, email, phone, city, rentValue, neighborhood]);

  return (
    <PageContainer id="anuncie-seu-imovel-form">
      <ContentWrapper>
        <InfoSection>
          <Title>Anuncie com quem entende de aluguel!</Title>
          <ul>
            <FeatureItem>
              <li> Consultores especializados no mercado imobiliário</li>
            </FeatureItem>
            <FeatureItem>
              <li> Fotos profissionais gratuitas</li>
            </FeatureItem>
            <FeatureItem>
              <li> Suporte na jornada de locação</li>
            </FeatureItem>
            <FeatureItem>
              <li> Aluguel garantido todos os meses</li>
            </FeatureItem>
            <FeatureItem>
              <li> Administração opcional</li>
            </FeatureItem>
          </ul>
        </InfoSection>

        <FormSection>
          <Title>Cadastre seu imóvel</Title>
          <form onSubmit={handleSubmit}>
            <Input
              type="text"
              placeholder="Endereço Completo"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            {validationError.address && <ErrorMessage>{validationError.address}</ErrorMessage>}

            <Input
              type="text"
              placeholder="Seu Nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {validationError.name && <ErrorMessage>{validationError.name}</ErrorMessage>}

            <Input
              type="email"
              placeholder="E-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {validationError.email && <ErrorMessage>{validationError.email}</ErrorMessage>}

            <Input
              type="tel"
              placeholder="Telefone (00) 00000-0000"
              value={phone}
              onChange={handlePhoneChange}
            />
            {validationError.phone && <ErrorMessage>{validationError.phone}</ErrorMessage>}

            <Input
              type="text"
              placeholder="Cidade"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            {validationError.city && <ErrorMessage>{validationError.city}</ErrorMessage>}

            <Input
              type="text"
              placeholder="Valor do Aluguel (ex: 3000)"
              value={rentValue}
              onChange={(e) => setRentValue(e.target.value)}
            />
            {validationError.rent_value && <ErrorMessage>{validationError.rent_value}</ErrorMessage>}

            <Input
              type="text"
              placeholder="Bairro"
              value={neighborhood}
              onChange={(e) => setNeighborhood(e.target.value)}
            />
            {validationError.neighborhood && <ErrorMessage>{validationError.neighborhood}</ErrorMessage>}

            {apiError && <ErrorMessage isGlobal>{apiError}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            <Button type="submit" disabled={loading}>
              {loading ? 'Enviando...' : 'Começar o Cadastro'}
            </Button>
          </form>

          <FormsParagr>
            Se preferir, entre em contato com nossa equipe para fazer seu
            cadastro:
          </FormsParagr>
          <ContactButtons>
            <ContactButton>
              <a href="tel:%2B551140037654"> Telefone </a>
            </ContactButton>
            <ContactButton>
              <a href="https://api.whatsapp.com/send/?phone=551140037654&text&type=phone_number&app_absent=0" target="_blank"> Whatsapp </a>
            </ContactButton>
          </ContactButtons>
        </FormSection>
      </ContentWrapper>
    </PageContainer>
  );
};

export default FormsProperty;